/**
 * Limpia y procesa los niveles que pueden estar en formato JSON anidado o mixto
 * Ejemplo: ["[\"Factor\"]"] -> ["Factor"]
 * Ejemplo: ["Factor", Característica, "Aspecto"] -> ["Factor", "Característica", "Aspecto"]
 */
export function limpiarNiveles(niveles: any): string[] {
  if (!niveles) return []

  try {
    // Paso 1: Si ya es array, procesar directamente
    if (Array.isArray(niveles)) {
      return niveles
        .map((n: any) => limpiarNivelIndividual(n))
        .filter((n: string) => n.length > 0)
    }

    // Paso 2: Convertir a string si es necesario
    let data = String(niveles).trim()

    // Paso 3: Si empieza con [ y termina con ], es un array representado como string
    if (data.startsWith('[') && data.endsWith(']')) {
      // Remover los corchetes externos
      data = data.slice(1, -1).trim()
      
      // Intentar parsear como JSON primero
      try {
        const parsed = JSON.parse(`[${data}]`)
        if (Array.isArray(parsed)) {
          return parsed
            .map((n: any) => limpiarNivelIndividual(n))
            .filter((n: string) => n.length > 0)
        }
      } catch {
        // Si no es JSON válido, continuamos con el split manual
      }

      // Split manual por coma, siendo cuidadosos con comillas
      return data
        .split(',')
        .map((n: string) => limpiarNivelIndividual(n.trim()))
        .filter((n: string) => n.length > 0)
    }

    // Paso 4: Desempacar JSON anidado
    let result = data
    let maxIterations = 10
    while (maxIterations > 0) {
      try {
        const parsed = JSON.parse(result)
        if (Array.isArray(parsed)) {
          return parsed
            .map((n: any) => limpiarNivelIndividual(n))
            .filter((n: string) => n.length > 0)
        } else if (typeof parsed === 'string') {
          result = parsed.trim()
          maxIterations--
          continue
        } else {
          break
        }
      } catch {
        break
      }
    }

    // Paso 5: Si no se pudo parsear, retornar como string individual limpio
    const cleaned = limpiarNivelIndividual(result)
    return cleaned ? [cleaned] : []
  } catch {
    return []
  }
}

/**
 * Formatea los niveles para mostrar al usuario de forma legible
 * Entrada: ["Factor", Característica, "Aspecto"] o ["[\"Factor\"]", "[\"Característica\"]"]
 * Salida: "Factor, Característica, Aspecto"
 */
export function formatearNivelesParaMostrar(niveles: any): string {
  if (!niveles) return ''
  
  try {
    // Primero limpiar los niveles
    const nivelesLimpios = limpiarNiveles(niveles)
    
    // Si está vacío, retornar cadena vacía
    if (nivelesLimpios.length === 0) return ''
    
    // Unir con coma y espacio
    return nivelesLimpios.join(', ')
  } catch {
    return ''
  }
}

/**
 * Limpia un nivel individual, eliminando comillas y espacios
 */
function limpiarNivelIndividual(nivel: any): string {
  if (!nivel) return ''

  let cleaned = String(nivel).trim()

  // Remover comillas dobles al inicio y final repetidamente
  while (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1).trim()
  }

  // Remover corchetes si están presentes
  if (cleaned.startsWith('[') && cleaned.endsWith(']')) {
    cleaned = cleaned.slice(1, -1).trim()
  }

  return cleaned
}
